import TodayIcon from '@mui/icons-material/Today';
import { Box, Button, Divider, FormControlLabel, Stack, Switch } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useGetPinForUserAcQuery, useGetUserCredentialsValidityAcQuery } from '../../apiRtk/accessControlApi';
import { useEditGuestInSiteMutation } from '../../apiRtk/be4feApi';
import { ReactComponent as TimeLimitedIcon } from '../../assets/time-limited.svg';
import { useGetLoadedParams } from '../../hooks/paramsHooks';
import { useGetGlobalInfo } from '../../hooks/useGetGlobalInfo';
import { useModal } from '../../hooks/useModal';
import { EnrichedApartmentUser } from '../../modules/apartmentAdmin/data';
import { getApartmentDevicesList } from '../../modules/apartments/actions';
import { getApartmentDevices } from '../../modules/apartments/selectors';
import { ApplicationContext } from '../../modules/commonTypes';
import { IDevice } from '../../modules/devices/store/deviceStore';
import { TYPE_DESCRIPTOR } from '../../modules/notifications';
import { addSnackbarError, addSnackbarSuccess } from '../../modules/notifications/actions';
import { getSiteData } from '../../modules/sites/selectors';
import { IconWithContent } from '../../pages/apartmentAdmin/ApartmentAdminUserDetailPage/components/UserAccessSettings/components/IconWithContent';
import { CustomDate } from '../index';
import RowSection from '../RowSection';
import { VisitorModalContext, VisitorModalOpeningContext } from '../VisitorModal/types';
import VisitorModal from '../VisitorModal/VisitorModal';
import messages from './messages';

interface ISectionAccessValidity {
  user: EnrichedApartmentUser;
}

function UserAccessValidity({ user }: ISectionAccessValidity) {
  const { dispatch, formatMessage } = useGetGlobalInfo();
  const { companyId, siteId } = useGetLoadedParams(ApplicationContext.UserDetail);
  const { apartments, id: userId, lastName } = user;
  const siteData = useSelector(getSiteData);
  const apartmentDevices = useSelector(getApartmentDevices).data || [];
  const [enrichedUser, setEnrichedUser] = useState(user);
  const [apartmentAccess, setApartmentAccess] = useState(false);
  const isVisitorValiditySet = enrichedUser.validFrom ?? enrichedUser.validTo;
  const { data: userCredentialsValidity, refetch: refetchValidity } = useGetUserCredentialsValidityAcQuery(
    {
      companyId: String(companyId),
      siteId: String(siteId),
      userId: String(userId),
    },
    { refetchOnMountOrArgChange: true }
  );
  const { data: pinForUser } = useGetPinForUserAcQuery({
    companyId: String(companyId),
    siteId: String(siteId),
    userId: String(userId),
  });
  const [editGuestInSite, { isLoading: isUpdatingAccess }] = useEditGuestInSiteMutation();

  const { Modal: VisitorUModal, onOpen: onOpenVisitorUModal } = useModal({
    Modal: VisitorModal,
  });

  const apartmentAccessPossible =
    !!apartments?.length &&
    pinForUser?.pin &&
    apartmentDevices.some((device: IDevice) => device.services?.ACCESS_CONTROL?.active);

  useEffect(() => {
    if (apartments?.length) {
      dispatch(getApartmentDevicesList(apartments[0].id, companyId, siteId));
    }
  }, [dispatch, apartments, companyId, siteId]);

  useEffect(() => {
    setEnrichedUser({
      ...user,
      validFrom: userCredentialsValidity?.validFrom,
      validTo: userCredentialsValidity?.validTo,
    });
  }, [userCredentialsValidity]);

  useEffect(() => {
    setApartmentAccess(userCredentialsValidity?.apartmentAccess ?? false);
  }, [userCredentialsValidity?.apartmentAccess]);

  const handleApartmentAccessToggle = (checked: boolean) => {
    if (!pinForUser?.pin) return;
    setApartmentAccess(checked);
    editGuestInSite({
      companyId,
      editGuestRequest: {
        apartmentAccess: checked,
        lastName: lastName ?? '',
        pin: pinForUser.pin,
        validFrom: userCredentialsValidity?.validFrom,
        validTo: userCredentialsValidity?.validTo,
      },
      siteId,
      userId,
    })
      .unwrap()
      .then(() => {
        dispatch(addSnackbarSuccess(messages.UserAccessAccessEditMsgSucc1));
        refetchValidity();
      })
      .catch(() => {
        setApartmentAccess(!checked);
        dispatch(addSnackbarError(messages.UserAccessAccessEditMsgErr1, TYPE_DESCRIPTOR));
      });
  };

  return (
    <>
      <RowSection
        icon={<IconWithContent Icon={TimeLimitedIcon}></IconWithContent>}
        label={formatMessage(messages.UserAccessValidityLabel)}
      >
        <Stack
          direction={{ sm: 'row', xs: 'column' }}
          justifyContent={{ sm: 'space-between', xs: 'flex-start' }}
          spacing={{ sm: 0, xs: 1 }}
          sx={{ width: 'inherit' }}
        >
          <>
            <Box>
              {isVisitorValiditySet ? (
                <>
                  {userCredentialsValidity?.validFrom && (
                    <>
                      <Box component="span" color={'text.secondary'}>
                        <FormattedMessage
                          {...messages.UserAccessValidityStart}
                          values={{
                            typography: (chunk) => <span>{chunk}</span>,
                            validityStart: (
                              <Typography component="span" color={'text.primary'}>
                                {CustomDate({ dateValue: userCredentialsValidity.validFrom, showTime: true })}
                              </Typography>
                            ),
                          }}
                        />
                      </Box>
                      &nbsp;
                    </>
                  )}
                  {userCredentialsValidity?.validTo && (
                    <Box component="span" color={'text.secondary'}>
                      <FormattedMessage
                        {...messages.UserAccessValidityEnd}
                        values={{
                          typography: (chunk) => <span>{chunk}</span>,
                          validityEnd: (
                            <Typography component="span" color={'text.primary'}>
                              {CustomDate({ dateValue: userCredentialsValidity.validTo, showTime: true })}
                            </Typography>
                          ),
                        }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography color={'text.secondary'}>
                  <FormattedMessage {...messages.UserAccessNoValidityLimit} />
                </Typography>
              )}
              {apartmentAccessPossible && (
                <Box pt={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={apartmentAccess}
                        color="warning"
                        disabled={isUpdatingAccess}
                        onChange={(event) => handleApartmentAccessToggle(event.target.checked)}
                      />
                    }
                    label={<FormattedMessage {...messages.UserAccessValidityEditModalAprtmDirectLabel} />}
                  />
                </Box>
              )}
            </Box>
            <Box>
              <Button onClick={onOpenVisitorUModal} startIcon={<TodayIcon />} variant="outlined">
                <FormattedMessage {...messages.UserAccessBtnValidityEdit} />
              </Button>
            </Box>
          </>
        </Stack>
      </RowSection>
      <Divider sx={{ mb: 3 }} />
      <VisitorUModal
        msgContext={VisitorModalContext.USER_ACCESS_VALIDITY_EDITMODAL}
        user={enrichedUser}
        apartmentInfo={{ companyId, siteId }}
        apartmentDevices={apartments.length ? apartmentDevices : []}
        siteName={siteData?.name}
        userCredentialsValidity={userCredentialsValidity}
        openingContext={VisitorModalOpeningContext.USER_ACCESS_VALIDITY}
      />
    </>
  );
}

export default UserAccessValidity;
